import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  mfaPending: boolean
  setMfaPending: (pending: boolean) => void
  signIn: (email: string, password: string) => Promise<{ data: { user: User | null } | null, error: AuthError | null }>
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>
  signInWithOAuth: (provider: 'google' | 'azure' | 'github' | 'discord') => Promise<{ error: AuthError | null }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [mfaPending, setMfaPending] = useState(false)

  useEffect(() => {
    // Initial session fetch
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Handle different auth events
      switch (event) {
        case 'SIGNED_IN':
        case 'TOKEN_REFRESHED':
        case 'USER_UPDATED':
          setSession(session)
          setUser(session?.user ?? null)
          break
        case 'SIGNED_OUT':
          setSession(null)
          setUser(null)
          setMfaPending(false)
          break
        default:
          setSession(session)
          setUser(session?.user ?? null)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    return { data: data ? { user: data.user } : null, error }
  }

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password })
    return { error }
  }

  const signInWithOAuth = async (provider: 'google' | 'azure' | 'github' | 'discord') => {
    // Define scopes for each provider to get user info
    const scopes: Record<string, string> = {
      google: 'openid email profile',
      azure: 'openid email profile User.Read',
      github: 'read:user user:email',
      discord: 'identify email'
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        scopes: scopes[provider]
      }
    })
    return { error }
  }

  const signOut = async () => {
    setMfaPending(false)
    await supabase.auth.signOut({ scope: 'global' })
  }

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      mfaPending,
      setMfaPending,
      signIn,
      signUp,
      signInWithOAuth,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
